// =============================================================================
// PRICE TABLE — the Settings/Account tab's FE mirror of apps/api/lib/
// priceTable.ts's PRICE_TABLE (PRICING LOCKED, R154, 2026-07-02, his word:
// $24.99/mo · $179/yr · $7.99/wk · $99 founder-year capped at first-100 ·
// 14-day trial on EVERY plan — docs/briefs/launch-week-briefs.md §B "PRICING
// — LOCKED" + §C "Lane C wires these as CONFIG (a price table, not
// literals)").
//
// This is a DISPLAY-ONLY mirror — the four plan cards render straight off
// this table (amountCents/interval/trialPeriodDays/founderCap), never a
// hand-typed literal in Settings.tsx. It is NOT the source of truth (the
// backend's own PRICE_TABLE + Stripe's actual Price objects are); it exists
// so the FE has one place to read the locked numbers from, and so a test
// (priceTable.test.ts) can assert byte-for-byte that this mirror has not
// drifted from the backend's table — the exact same "config mirror, tested
// for drift" discipline api.ts's JobRecord/PollSummary/etc. already follow
// for every OTHER backend shape this app renders (see that file's own
// header: "COPIED verbatim... nothing here is a new field").
//
// soldOut is NOT a field on this table — whether founder is sold out is a
// LIVE fact only the backend knows (a COUNT query against `subscriptions`),
// never something the FE could compute or cache locally. The checkout
// button always calls POST /api/billing/checkout and renders whatever the
// server actually says (see useSettings.ts's runCheckout) — {soldOut:true}
// is a possible response, not a client-side flag on this config.
// =============================================================================

import type { SubscriptionPlan } from '@careeropt/schema';

export interface DisplayPricePlan {
  plan: SubscriptionPlan;
  /** Cents — DISPLAY only (mirrors apps/api/lib/priceTable.ts's own doc
   *  comment: "Stripe's own Price object is the actual billing source of
   *  truth; this number must never drift... but nothing in this codebase
   *  enforces that except a human keeping them in sync"). */
  amountCents: number;
  currency: 'usd';
  interval: 'week' | 'month' | 'year';
  trialPeriodDays: number;
  /** Only 'founder' sets this — the first-100 cap (PRICING LOCKED). Absent
   *  (not 0/null) on every other plan, exactly like the backend table. */
  founderCap?: number;
}

/** The four locked plans, byte-for-byte identical to apps/api/lib/
 *  priceTable.ts's PRICE_TABLE for every field this FE needs to render a
 *  card (amountCents/currency/interval/trialPeriodDays/founderCap) — the
 *  ONE field deliberately NOT mirrored is priceIdEnvVar (a server-only
 *  concept: which Bindings key holds the REAL Stripe price id — the FE
 *  never needs to know that, it only ever sends the plan NAME to POST
 *  /billing/checkout and renders whatever {configured, checkoutUrl,
 *  soldOut, reason} the server returns). */
export const DISPLAY_PRICE_TABLE: Record<SubscriptionPlan, DisplayPricePlan> = {
  weekly: {
    plan: 'weekly',
    amountCents: 799,
    currency: 'usd',
    interval: 'week',
    trialPeriodDays: 14,
  },
  monthly: {
    plan: 'monthly',
    amountCents: 2499,
    currency: 'usd',
    interval: 'month',
    trialPeriodDays: 14,
  },
  annual: {
    plan: 'annual',
    amountCents: 17900,
    currency: 'usd',
    interval: 'year',
    trialPeriodDays: 14,
  },
  founder: {
    plan: 'founder',
    amountCents: 9900,
    currency: 'usd',
    interval: 'year',
    trialPeriodDays: 14,
    founderCap: 100,
  },
};

/** The brief's own display order (§B: "$24.99/mo · $179/yr · $7.99/wk ·
 *  $99 founder") is price-anchoring order, not plan-card order — the cards
 *  render in this order instead: weekly (lowest commitment) → monthly
 *  (the anchor plan) → annual (the value plan) → founder (the scarce,
 *  capped one) last, so its {cap: 100, soldOut} framing reads as a special
 *  callout rather than the first thing a new visitor sees. */
export const DISPLAY_PLAN_ORDER: SubscriptionPlan[] = ['weekly', 'monthly', 'annual', 'founder'];

/** `amountCents` -> "$24.99" (no trailing ".00" is trimmed — every locked
 *  price has cents, so this never needs to special-case a whole-dollar
 *  amount). Pure formatting, no locale library — this app has no other
 *  currency-formatting need yet. */
export function formatUsd(amountCents: number): string {
  return `$${(amountCents / 100).toFixed(2)}`;
}

/** 'week' -> 'wk', 'month' -> 'mo', 'year' -> 'yr' — mirrors the brief's own
 *  abbreviations ("$24.99/mo · $179/yr · $7.99/wk"). */
export function intervalAbbrev(interval: DisplayPricePlan['interval']): string {
  if (interval === 'week') return 'wk';
  if (interval === 'month') return 'mo';
  return 'yr';
}

/** Human label per plan — used as the card title. Not derived from the
 *  plan enum string (weekly/monthly/annual/founder already read fine
 *  capitalized, but 'founder' alone doesn't say "capped at first 100" —
 *  that qualifier lives in the card body, reading founderCap directly off
 *  DISPLAY_PRICE_TABLE, never hand-typed as a duplicate "100" literal). */
export const PLAN_LABEL: Record<SubscriptionPlan, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  annual: 'Annual',
  founder: 'Founder',
};
